import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import AppShell from "../components/shell/AppShell";
import { useStateContext } from "../contexts/ContextProvider";
import { useAuth } from "../contexts/AuthContext";
import { WebSocketProvider } from "../context/WebSocketProvider";
import Dashboard from "./Dashboard";
// import EditBrokerAccount from "./Gateways/EditTradeAccount/EditBrokerAccount";

// Import pages for routes
import TradeSetup from "./Trades/TradeSetup";

// Import new Trade Accounts components
import TradeAccounts from "./BrokerPlatforms/TradeAccounts";
import AddEditTradeAccount from "./BrokerPlatforms/AddOrEditTradeAccount/AddEditTradeAccount";

import BacktestRunsPage from "./Strategies/Modern/Backtesting/BacktestRunsPage";
import Orders from "./Trades/Orders";
import AlgoDashboard from "./AlgoTrading/AlgoDashboard";
import StrategyDeepDive from "./AlgoTrading/StrategyDeepDive";
import TradingDataLayout from "../contexts/TradingDataLayout";
import WatchList from "./WatchList/WatchList";
import SmartTrade from "./Trades/SmartTrade/SmartTrade";
import StrategyHub from "./Strategies/Modern/StrategyHub";
import StrategyLineage from "./Strategies/Modern/StrategyLineage";

// Import Admin pages
import { LiveConnections, InstrumentMaster, UserRoleManagement, BrokerManagement, MarketDataManagement, BatchJobs, AdminRecoveryPanel, ModelsPage, TradeEconomicsPage } from "./Admin";

// 104 Element R (D67/R12) — the PRIMARY user-facing self-service recovery screen.
import StuckOrdersPanel from "./Trades/StuckOrders/StuckOrdersPanel";
import KillSwitchBanner from "../components/trading/KillSwitchBanner";

const MainLayout = () => {
  const { activeMenu } = useStateContext();
  const { user } = useAuth();

  // Track access token in state to ensure WebSocketProvider updates when token is refreshed
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem("accessToken"));

  // Listen for storage changes (cross-tab sync) and check for token updates periodically
  useEffect(() => {
    // Handler for storage events from other tabs
    const handleStorageChange = (e) => {
      if (e.key === "accessToken") {
        console.log("🔄 [MainLayout] Access token changed in storage, updating WebSocket");
        setAccessToken(e.newValue);
      }
    };

    // Periodic check for token changes within the same tab
    // This catches silent token refreshes from AuthContext
    const tokenCheckInterval = setInterval(() => {
      const currentToken = localStorage.getItem("accessToken");
      if (currentToken !== accessToken) {
        console.log("🔄 [MainLayout] Access token refreshed, updating WebSocket provider");
        setAccessToken(currentToken);
      }
    }, 5000); // Check every 5 seconds

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(tokenCheckInterval);
    };
  }, [accessToken]);

  // Dispatch a resize event when the sidebar state changes to allow charts/grids to re-render correctly
  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 600); // Wait for both Sidebar (300ms) and Right Pane (500ms) transitions
    return () => clearTimeout(timer);
  }, [activeMenu]); // Assuming isRightPaneCollapsed and isMetricsExpanded are not available in this context

  return (
    <WebSocketProvider
      authToken={accessToken}
      userId={user?.id || user?.user_id}
    >
      {/* #44: live-data trouble is surfaced by <LiveDataStatus /> in the Navbar,
          not by a full-width banner here — the banner covered the app chrome and
          shouted on every page for a condition the navbar can hold quietly. */}
      <AppShell>
          {/* #133 — user-scope kill-switch: renders nothing unless ACTIVE (then a red band
              with the trip reason + "Resume trading"). Polls every 20 s. */}
          <KillSwitchBanner />
          <Routes>
            {/* Dashboard */}
            <Route path="/" element={<Navigate to="/Overview" replace />} />
            <Route path="/Overview" element={<Dashboard />} />
            <Route path="/Portfolio" element={<Dashboard />} />

            {/* Watch List */}
            <Route path="/WatchList" element={<WatchList />} />
            
            {/* New Smart Trade Page */}
            <Route path="/SmartTrade" element={<SmartTrade />} />

            {/* Manual Trading */}
            <Route path="/TradeSetup" element={<TradeSetup />} />

            {/* /Orders + Algo Trading — wrapped in TradingDataProvider via layout route.
                Orders.jsx (Slice 3 consolidated view) reads TradingDataContext from this
                ancestor layout and does NOT mount its own provider, so it MUST be nested
                here (R102.1 AC(b) — provider above the page so tab switches don't remount it). */}
            <Route element={<TradingDataLayout />}>
              <Route path="/Orders" element={<Orders />} />
              <Route path="/AlgoDashboard" element={<AlgoDashboard />} />
              <Route path="/AlgoDashboard/:deploymentId" element={<StrategyDeepDive />} />
            </Route>

            {/* 104 Element R (D67/R12) — user self-service recovery, reachable from
                Orders, NOT the Admin section (D67: the user is the primary path). */}
            <Route path="/StuckOrders" element={<StuckOrdersPanel />} />

            {/* Strategy */}
            <Route path="/Strategies/Modern" element={<StrategyHub />} />
            <Route path="/Strategies/Modern/lineage/:projectId" element={<StrategyLineage />} />
            <Route path="/Strategies/Modern/Backtesting" element={<BacktestRunsPage />} />

            {/* Broker Platforms / Trade Accounts */}
            <Route path="/TradeAccounts" element={<TradeAccounts />} />
            <Route path="/AddTradeAccount" element={<AddEditTradeAccount />} />
            <Route
              path="/AddTradeAccount/:accountId"
              element={<AddEditTradeAccount />}
            />
            <Route
              path="/TradeAccounts/:accountId"
              element={<TradeAccounts />}
            />

            {/* Admin */}
            <Route path="/Admin" element={<LiveConnections />} />
            <Route path="/Admin/LiveConnections" element={<LiveConnections />} />
            <Route path="/Admin/BatchJobs" element={<BatchJobs />} />
            <Route path="/Admin/Models" element={<ModelsPage />} />
            <Route path="/Admin/TradeEconomics" element={<TradeEconomicsPage />} />
            {/* 104 Element R (D67/R12.3) — admin backup, secondary to /StuckOrders above. */}
            <Route path="/Admin/StuckOrders" element={<AdminRecoveryPanel />} />
            <Route path="/InstrumentMaster" element={<InstrumentMaster />} />
            <Route path="/user-management" element={<UserRoleManagement />} />
            <Route path="/BrokerManagement" element={<BrokerManagement />} />
            <Route path="/MarketDataManagement" element={<MarketDataManagement />} />


            {/* Redirect any other routes to dashboard */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
      </AppShell>
    </WebSocketProvider>
  );
};

export default MainLayout;
